import { Form, Formik, FormikProps } from "formik";
import { useEffect, useMemo, useState } from "react";
import { getNames } from "country-list";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { SearchableSelect } from "../../components/shared/SearchableSelect";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  Center,
  Error,
  HStack,
  VStack,
  isCompanySettingsReadOnly,
  isEsopViewer,
  decodeAuthToken,
} from "../../components/utils";
import {
  companyClassDropdownValues,
  currencyDropDownValues,
} from "../../constants/DropDownValues";
import {
  useApproveBonus,
  useApproveSplit,
  useApproveBonusRatioChange,
  useCompanyProfileDetails,
  useGetAllCorporateActions,
  useUpdateCompanyProfile,
} from "../../queries/onBoarding";
import BCHeader from "../../shared/BCHeader";
import { usePermissionStore } from "../../store/permissionStore";
import { CompanyProfileDetails } from "../../types/SiteSettings";
import { determineUserAccessToResource } from "../../utils/auth";
import { formatDate } from "../../utils/date";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { initialCompanyDetail } from "./initValues";
import ImageResizer from "../../components/shared/ImageResizer";
import { useGetUserCompanies, useUserDetails } from "../../queries";
import { useAuthStore } from "../../store";
import AllCorporateActionDetails from "./AllCorporateActionDetails";
import { CorporateActionDetails } from "../../types/CompanyProfile";
import { AddOrEditBonusDetails } from "../../modals/AddOrEditBonusDetails";
import DropdownButton from "../../components/shared/DropdownButton";
import { Action } from "../../components/shared/Dropdown";
import AlertDialog from "../../components/shared/AlertDialog";
import PaymentDetailsDialog, {
  CompanyPaymentDetails,
} from "./PaymentDetailsDialog";
import { useGetCompanyPaymentDetails } from "../../queries/payment";
import { AddOrEditSplitDetails } from "./AddOrEditSplitDetails";
import { AddOrEditBonusRatioChangeDetails } from "./AddOrEditBonusRatioChangeDetails";
import { BonusTypeSelector } from "./BonusTypeSelector";
import { useIsInternalUser } from "../../utils/useIsInternalUser";

export interface FileTypeModel {
  fileBlob: string;
  fileName?: string;
  fileType?: string;
}
export function CompanyProfile() {
  const isUserEsopViewer = isEsopViewer();
  const isUserReadOnly = isCompanySettingsReadOnly();
  const { data: _companyDetail } = useCompanyProfileDetails();
  const { mutate: updateCompanyDetails, isLoading } = useUpdateCompanyProfile();
  const companyDetail = _companyDetail;
  const { data: user } = useUserDetails();
  const { data: userCompanies } = useGetUserCompanies();
  const { accessToken } = useAuthStore();
  const isRuleZeroUser = useIsInternalUser();
  const companyOptions = (userCompanies?.associatedCompanies || [])
    .filter((company) => company.id !== companyDetail?.id)
    .map((company) => ({ label: company.name, value: company.id }));
  const [isEdit, setIsEdit] = useState(false);
  const { permission } = usePermissionStore();
  const [imageResizerProps, setImageResizerProps] = useState<{
    open: boolean;
    imageUrl?: string;
    orientation?: "portrait" | "landscape";
  }>({ open: false, imageUrl: undefined, orientation: "portrait" });

  const { data: companyPaymentDetails } = useGetCompanyPaymentDetails();

  const getInitialValue = () =>
    companyDetail
      ? {
          id: companyDetail.id ?? "",
          name: companyDetail.name ?? "",
          cin: companyDetail.cin ?? "",
          companyCurrency: companyDetail.companyCurrency ?? "",
          registeredCountry: companyDetail.registeredCountry ?? "India",
          dateOfIncorporation: formatDate(companyDetail.dateOfIncorporation),
          address: companyDetail.address ?? "",
          email: companyDetail.email ?? "",
          companyLogoPath: companyDetail.companyLogoPath ?? "",
          operatingAs: companyDetail.operatingAs ?? "",
          nic: companyDetail.nic ?? "",
          typeOfTheCompany: companyDetail.typeOfTheCompany ?? "",
          stockSymbol: companyDetail.stockSymbol ?? "",
          parentCompanyId: companyDetail.parentCompanyId ?? "",
          subsidiaryCompanyIds: companyDetail.subsidiaryCompanyIds ?? [],
          companyLogoDisplaySettings:
            companyDetail.companyLogoDisplaySettings ?? {
              orientation: "portrait",
              displayFormat: "logoOnly",
            },
        }
      : initialCompanyDetail;
  const handleCompanyDetailsSubmit = (values: CompanyProfileDetails) => {
    updateCompanyDetails(values, {
      onSuccess: () => {
        toast("Company Profile updated Successfully!", {
          type: "success",
          autoClose: 2000,
        });
      },
      onError: (error: { response: { data: { reason: string } } }) => {
        toast(`${error.response.data.reason}`, {
          type: "error",
          autoClose: 2000,
        });
      },
    });
  };
  const validationCompanyDetailsSchema = Yup.object().shape({
    name: Yup.string().required("Company Name is required"),
    cin: Yup.string().required("CIN Number is required"),
    address: Yup.string().required("Company Registered Address is required"),
    dateOfIncorporation: Yup.string().required(
      "Date of Incorporation is required"
    ),
    email: Yup.string()
      .email("Enter Valid Company Email")
      .required("Company Email is required"),
    companyCurrency: Yup.string().required("Company Currency is required"),
    registeredCountry: Yup.string().required("Registered country is required"),
    nic: Yup.string().required("Company NIC is required"),
    operatingAs: Yup.string().required("Doing Business As is required"),
    typeOfTheCompany: Yup.string().label("Class of the company"),
    stockSymbol: Yup.string().label("Stock Symbol"),
  });
  function handleEditImage(
    formikInstance: FormikProps<CompanyProfileDetails>
  ): void {
    setImageResizerProps({
      open: true,
      imageUrl: formikInstance.values.companyLogoPath,
    });
  }
  function handleImageSave(
    image: string | undefined,
    formikInstance: FormikProps<CompanyProfileDetails>,
    orientationSettings?: CompanyProfileDetails["companyLogoDisplaySettings"]
  ) {
    if (image) {
      formikInstance.setFieldValue("companyLogoPath", image);
    }
    if (orientationSettings) {
      formikInstance.setFieldValue(
        "companyLogoDisplaySettings",
        orientationSettings
      );
    }
    setImageResizerProps({ open: false, imageUrl: undefined });
  }

  const { data, refetch: getAllCorporateActions } = useGetAllCorporateActions(
    _companyDetail?.id || ""
  );
  const { corporateActions } = data || { corporateActions: [] };

  const [showCorporateActions, setShowCorporateActions] = useState<boolean>();

  useEffect(() => {
    setShowCorporateActions(!(data?.userHasLimitedAccessToCompany || false));
  }, [data?.userHasLimitedAccessToCompany]);

  useEffect(() => {
    getAllCorporateActions();
  }, [_companyDetail?.id]);

  useEffect(() => {
    if (
      bonusRatioChangeDialog.open &&
      bonusRatioChangeDialog.data?.id &&
      corporateActions
    ) {
      const updated = corporateActions.find(
        (ca) => ca.id === bonusRatioChangeDialog.data?.id
      );
      if (updated) {
        setBonusRatioChangeDialog((prev) => ({ ...prev, data: updated }));
      }
    }
  }, [corporateActions]);

  const [bonusTypeDialog, setBonusTypeDialog] = useState(false);

  const [dialog, setDialog] = useState<{
    open: boolean;
    data?: CorporateActionDetails;
    mode?: "EditBonus" | "ApproveBonus";
  }>({
    open: false,
  });

  const [splitDialog, setSplitDialog] = useState<{
    open: boolean;
    data?: CorporateActionDetails;
    mode?: "AddSplit" | "EditSplit" | "ApproveSplit";
  }>({
    open: false,
  });

  const [bonusRatioChangeDialog, setBonusRatioChangeDialog] = useState<{
    open: boolean;
    data?: CorporateActionDetails;
    mode?:
      | "AddBonusRatioChange"
      | "EditBonusRatioChange"
      | "ApproveBonusRatioChange";
  }>({
    open: false,
  });

  function handleCorporateAction(action: Action) {
    if (action.name === "Add Bonus" && !action.disabled) {
      setBonusTypeDialog(true);
    }
    if (action.name === "Add Split" && !action.disabled) {
      setSplitDialog({
        open: true,
        mode: "AddSplit",
      });
    }
  }

  const [paymentDialog, setPaymentDialog] = useState<{
    open: boolean;
    data?: CompanyPaymentDetails;
    mode?: "create" | "edit";
  }>({
    open: false,
  });

  function openBonusDialog(corporationAction: CorporateActionDetails) {
    setDialog({
      open: true,
      mode: "EditBonus",
      data: corporationAction,
    });
  }

  function openSplitDialog(corporationAction: CorporateActionDetails) {
    setSplitDialog({
      open: true,
      mode: "EditSplit",
      data: corporationAction,
    });
  }

  function openBonusRatioChangeDialog(
    corporationAction: CorporateActionDetails
  ) {
    setBonusRatioChangeDialog({
      open: true,
      mode: "EditBonusRatioChange",
      data: corporationAction,
    });
  }

  const [isApproveLoading, setIsApproveLoading] = useState(false);

  const { mutate: approveBonus } = useApproveBonus();
  const { mutate: approveSplit } = useApproveSplit();
  const { mutate: approveBonusRatioChange } = useApproveBonusRatioChange();

  function handleApproveBonus(values?: CorporateActionDetails) {
    setIsApproveLoading(true);
    approveBonus(values?.id || "", {
      onSuccess: (data) => {
        toast("Bonus Approved Successfully", {
          type: "success",
          autoClose: 2000,
        });
        setIsApproveLoading(false);
        setDialog({ open: false });
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorRaw;
        toast(errMsg || "Error Creating Bonus Details", {
          type: "error",
          autoClose: 2000,
        });
        setIsApproveLoading(false);
      },
    });
  }

  function handleApproveSplit(values?: CorporateActionDetails) {
    setIsApproveLoading(true);
    approveSplit(values?.id || "", {
      onSuccess: (data) => {
        toast("Split Approved Successfully", {
          type: "success",
          autoClose: 2000,
        });
        setIsApproveLoading(false);
        setSplitDialog({ open: false });
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorRaw;
        toast(errMsg || "Error Creating Split Details", {
          type: "error",
          autoClose: 2000,
        });
        setIsApproveLoading(false);
      },
    });
  }

  function handleApproveBonusRatioChange(values?: CorporateActionDetails) {
    setIsApproveLoading(true);
    approveBonusRatioChange(values?.id || "", {
      onSuccess: () => {
        toast("Bonus Ratio Change Approved Successfully", {
          type: "success",
          autoClose: 2000,
        });
        setIsApproveLoading(false);
        setBonusRatioChangeDialog({ open: false });
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorRaw;
        toast(errMsg || "Error Approving Bonus Ratio Change", {
          type: "error",
          autoClose: 2000,
        });
        setIsApproveLoading(false);
      },
    });
  }

  return (
    <div className="gap-4">
      <Dialog open={bonusTypeDialog} maxWidth="md" fullWidth>
        {bonusTypeDialog && (
          <BonusTypeSelector
            onClose={() => setBonusTypeDialog(false)}
            setDialog={(state: any) => {
              setBonusTypeDialog(false);
              setDialog(state);
            }}
            setBonusRatioChangeDialog={(state: any) => {
              setBonusTypeDialog(false);
              setBonusRatioChangeDialog(state);
            }}
          />
        )}
      </Dialog>
      <Dialog open={dialog.open} maxWidth="md">
        {dialog.mode === "EditBonus" && dialog.data ? (
          <AddOrEditBonusDetails
            corporateAction={dialog.data}
            onClose={() => setDialog({ open: false })}
            mode={dialog.mode}
            setDialog={setDialog}
          />
        ) : dialog.mode === "ApproveBonus" ? (
          <AlertDialog
            message={`Are you sure you want to Approve Bonus ?`}
            primaryActionText="Approve"
            onClose={() => setDialog({ open: false })}
            secondaryActionText="Back"
            onPrimaryAction={() => {
              handleApproveBonus(dialog.data);
            }}
            onSecondaryAction={() => {
              setDialog({ open: true, data: dialog.data, mode: "EditBonus" });
            }}
            open={dialog.open}
            loading={isApproveLoading}
          />
        ) : (
          <></>
        )}
      </Dialog>
      <Dialog
        open={splitDialog.open}
        fullWidth
        PaperProps={{
          style: {
            width: "100%",
            maxWidth: "calc(100vw - 50px)",
            margin: "16px auto", // vertical margin for spacing
            boxSizing: "border-box",
          },
        }}
      >
        {splitDialog.mode === "AddSplit" ||
        (splitDialog.mode === "EditSplit" && splitDialog.data) ? (
          <div className="">
            <AddOrEditSplitDetails
              corporateAction={splitDialog.data}
              onClose={() => setSplitDialog({ open: false })}
              mode={splitDialog.mode}
              setDialog={setSplitDialog}
            />
          </div>
        ) : splitDialog.mode === "ApproveSplit" ? (
          <AlertDialog
            message={`Are you sure you want to Approve Split ?`}
            primaryActionText="Approve"
            onClose={() => setSplitDialog({ open: false })}
            secondaryActionText="Back"
            onPrimaryAction={() => {
              handleApproveSplit(splitDialog.data);
            }}
            onSecondaryAction={() => {
              setSplitDialog({
                open: true,
                data: splitDialog.data,
                mode: "EditSplit",
              });
            }}
            open={splitDialog.open}
            loading={isApproveLoading}
          />
        ) : (
          <></>
        )}
      </Dialog>
      <Dialog open={bonusRatioChangeDialog.open} maxWidth="md" fullWidth>
        {bonusRatioChangeDialog.mode === "AddBonusRatioChange" ||
        (bonusRatioChangeDialog.mode === "EditBonusRatioChange" &&
          bonusRatioChangeDialog.data) ? (
          <AddOrEditBonusRatioChangeDetails
            corporateAction={bonusRatioChangeDialog.data}
            onClose={() => setBonusRatioChangeDialog({ open: false })}
            mode={bonusRatioChangeDialog.mode}
            setDialog={setBonusRatioChangeDialog}
          />
        ) : bonusRatioChangeDialog.mode === "ApproveBonusRatioChange" ? (
          <AlertDialog
            message={`Are you sure you want to Approve this Bonus Ratio Change? This will apply ratio changes to all filtered grants.`}
            primaryActionText="Approve"
            onClose={() => setBonusRatioChangeDialog({ open: false })}
            secondaryActionText="Back"
            onPrimaryAction={() => {
              handleApproveBonusRatioChange(bonusRatioChangeDialog.data);
            }}
            onSecondaryAction={() => {
              setBonusRatioChangeDialog({
                open: true,
                data: bonusRatioChangeDialog.data,
                mode: "EditBonusRatioChange",
              });
            }}
            open={bonusRatioChangeDialog.open}
            loading={isApproveLoading}
          />
        ) : (
          <></>
        )}
      </Dialog>
      {showCorporateActions && (corporateActions || []).length > 0 && (
        <Box className="py-7">
          <AllCorporateActionDetails
            corporateActions={corporateActions}
            handleCorporateAction={handleCorporateAction}
            openBonusDialog={openBonusDialog}
            openSplitDialog={openSplitDialog}
            openBonusRatioChangeDialog={openBonusRatioChangeDialog}
          />
        </Box>
      )}
      <Formik
        enableReinitialize={true}
        initialValues={getInitialValue()}
        onSubmit={(values: CompanyProfileDetails) => {
          handleCompanyDetailsSubmit(values);
        }}
        validationSchema={validationCompanyDetailsSchema}
      >
        {(formik) => (
          <>
            <Form>
              <div className="w-full bg-white rounded-md">
                <Box className="px-10 py-4 text-lg font-medium text-left border-b">
                  <HStack className="justify-between">
                    <HStack className="justify-start">
                      <h6>Company Details</h6>
                    </HStack>
                    <HStack className="justify-end gap-8">
                      <HStack>
                        {showCorporateActions &&
                          (corporateActions || []).length === 0 && (
                            <DropdownButton
                              className="items-center text-sm"
                              actions={[
                                {
                                  name: "Add Bonus",
                                  disabled:
                                    !determineUserAccessToResource(
                                      permission?.aclList || [],
                                      Resource.All,
                                      DefaultAction.Edit
                                    ) || isUserReadOnly,
                                },
                                {
                                  name: "Add Split",
                                  disabled:
                                    !determineUserAccessToResource(
                                      permission?.aclList || [],
                                      Resource.All,
                                      DefaultAction.Edit
                                    ) || isUserReadOnly,
                                },
                              ]}
                              onAction={handleCorporateAction}
                              render={() => (
                                <div className="flex items-center rounded">
                                  <p>Corporate Action</p>
                                  <Icon
                                    height={24}
                                    icon="ri:arrow-drop-down-line"
                                    className="inline"
                                  />
                                </div>
                              )}
                              subtitle="Corporate Actions"
                            />
                          )}
                      </HStack>
                      <div>
                        {paymentDialog.open && (
                          <Dialog maxWidth="lg" open={paymentDialog.open}>
                            <div className="w-[800px] mx-auto bg-white rounded-lg  min-h-min">
                              <PaymentDetailsDialog
                                onClose={() =>
                                  setPaymentDialog({ open: false })
                                }
                                data={paymentDialog.data}
                                mode={paymentDialog.mode}
                              />
                            </div>
                          </Dialog>
                        )}
                      </div>
                    </HStack>
                  </HStack>
                </Box>
                <VStack className="w-full py-4">
                  <VStack className="gap-4 mx-auto">
                    <Box
                      className={`relative ${
                        formik.values.companyLogoDisplaySettings
                          ?.orientation === "portrait"
                          ? "w-32 h-32"
                          : "w-80 h-24"
                      }`}
                    >
                      <Label>
                        <Icon
                          icon={"eva:edit-outline"}
                          onClick={() => isEdit && handleEditImage(formik)}
                          className={`${
                            !isEdit ? "cursor-not-allowed" : ""
                          } absolute z-10 w-6 h-6 text-gray-400 bg-gray-100 rounded-full cursor-pointer -right-3 hover:text-black`}
                        />
                      </Label>
                      <Box className="relative">
                        {formik.values?.companyLogoPath === "" ? (
                          <Center
                            className={`bg-gray-100 ${
                              formik.values.companyLogoDisplaySettings
                                ?.orientation === "portrait"
                                ? "w-32 h-32"
                                : "w-80 h-24"
                            } rounded-lg`}
                          >
                            <Center
                              className={`flex ${
                                formik.values.companyLogoDisplaySettings
                                  ?.orientation === "portrait"
                                  ? "w-32 h-32"
                                  : "w-40 h-24"
                              } rounded-full`}
                            >
                              <p className="text-base font-bold">
                                {formik.values.name.charAt(0)}
                              </p>
                            </Center>
                          </Center>
                        ) : (
                          <img
                            src={formik.values.companyLogoPath}
                            alt="avatar"
                            className={`block bg-gray-400 border-2 rounded-lg
        ${
          formik.values.companyLogoDisplaySettings?.orientation === "portrait"
            ? "w-32 h-32"
            : "w-80 h-24"
        }
        ${
          formik.values.companyLogoDisplaySettings?.displayFormat === "logoOnly"
            ? "object-cover"
            : "object-contain"
        }
      `}
                            style={{
                              objectFit:
                                formik.values.companyLogoDisplaySettings
                                  ?.displayFormat === "logoOnly"
                                  ? "cover"
                                  : "contain",
                            }}
                          />
                        )}
                      </Box>

                      <Icon
                        icon={"eva:close-fill"}
                        className={`${
                          isEdit ? "cursor-not-allowed" : ""
                        } absolute z-10 w-6 h-6 text-gray-400 bg-gray-100 rounded-full cursor-pointer -right-3 -bottom-3 hover:text-black`}
                      />
                    </Box>
                  </VStack>
                </VStack>
                <VStack className="w-full gap-6 px-10 py-4">
                  <VStack className="gap-2">
                    <HStack className="gap-8">
                      <div className="flex-1">
                        <Label className="text-sm font-normal">
                          Name of the Company
                          <span className="text-rose-400">*</span>
                        </Label>
                        <Input
                          type="text"
                          disabled={!isEdit}
                          placeholder="Enter Company Name"
                          {...formik.getFieldProps("name")}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <Error text={formik.errors.name?.toString()} />
                        )}
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm font-normal">
                          CIN Number
                          <span className="text-rose-400">*</span>
                        </Label>
                        <Input
                          type="text"
                          disabled={!isEdit}
                          className=""
                          {...formik.getFieldProps("cin")}
                        />
                        {formik.touched.cin && formik.errors.cin && (
                          <Error text={formik.errors.cin?.toString()} />
                        )}
                      </div>
                    </HStack>
                  </VStack>
                  <HStack className="gap-8">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Company Email ID
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Input
                        type="text"
                        disabled={!isEdit}
                        placeholder="Eg: example@domain.com"
                        {...formik.getFieldProps("email")}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <Error text={formik.errors.email?.toString()} />
                      )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Date of Incorporation
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Input
                        type="date"
                        placeholder="Eg:12-12-1212"
                        disabled={!isEdit}
                        {...formik.getFieldProps("dateOfIncorporation")}
                      />
                      {formik.touched.dateOfIncorporation &&
                        formik.errors.dateOfIncorporation && (
                          <Error
                            text={formik.errors.dateOfIncorporation?.toString()}
                          />
                        )}
                    </div>
                  </HStack>
                  <HStack className="gap-8">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Corporate Office Address
                      </Label>
                      <TextArea
                        placeholder="Enter Your Company Address"
                        disabled={!isEdit}
                        {...formik.getFieldProps("address")}
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Company Currency
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Select
                        options={currencyDropDownValues}
                        isPlaceHolderDisabled={true}
                        disabled={!isEdit}
                        {...formik.getFieldProps("companyCurrency")}
                      />
                      {formik.touched.companyCurrency &&
                        formik.errors.companyCurrency && (
                          <Error
                            text={formik.errors.companyCurrency?.toString()}
                          />
                        )}
                    </div>
                  </HStack>
                  <HStack className="gap-8">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        NIC
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Input
                        type="text"
                        disabled={!isEdit}
                        {...formik.getFieldProps("nic")}
                      />
                      {formik.touched.nic && formik.errors.nic && (
                        <Error text={formik.errors.nic?.toString()} />
                      )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Doing Business As
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Input
                        type="text"
                        disabled={!isEdit}
                        {...formik.getFieldProps("operatingAs")}
                      />
                      {formik.touched.nic && formik.errors.nic && (
                        <Error text={formik.errors.operatingAs?.toString()} />
                      )}
                    </div>
                  </HStack>
                  <HStack className="gap-8">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Class (Type Of The Company)
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Select
                        options={companyClassDropdownValues}
                        valueGetter={(value) => value.value}
                        textGetter={(value) => value.label}
                        isPlaceHolderDisabled={true}
                        {...formik.getFieldProps("typeOfTheCompany")}
                      />
                      {formik.touched.typeOfTheCompany &&
                        formik.errors.typeOfTheCompany && (
                          <Error
                            text={formik.errors.typeOfTheCompany?.toString()}
                          />
                        )}
                    </div>
                    <div className="flex-1">
                      {formik.values.typeOfTheCompany === "PUBLIC" && (
                        <HStack className="gap-8">
                          <div className="flex-1">
                            <Label className="text-sm font-normal">
                              Stock Symbol
                            </Label>
                            <Input
                              type="text"
                              {...formik.getFieldProps("stockSymbol")}
                            />
                            {formik.touched.stockSymbol &&
                              formik.errors.stockSymbol && (
                                <Error
                                  text={formik.errors.stockSymbol?.toString()}
                                />
                              )}
                          </div>
                          <div className="flex-1" />
                        </HStack>
                      )}
                    </div>
                  </HStack>
                  {isRuleZeroUser && (
                    <VStack className="gap-2">
                      <HStack className="gap-8">
                        <div className="flex-1">
                          <Label className="text-sm font-normal">
                            Parent company
                          </Label>
                          <SearchableSelect
                            options={companyOptions}
                            placeholder="Select a parent company"
                            valueGetter={(option) => option.value}
                            textGetter={(option) => option.label}
                            value={formik.values.parentCompanyId}
                            disabled={!isEdit}
                            onChange={(event) =>
                              formik.setFieldValue(
                                "parentCompanyId",
                                event.target.value
                              )
                            }
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm font-normal">
                            Subsidiary companies
                          </Label>
                          <MultiSelectV2
                            multiple
                            searchable
                            placeholder="Select subsidiary companies"
                            options={companyOptions.filter(
                              (company) =>
                                company.value !== formik.values.parentCompanyId
                            )}
                            optionValues={
                              formik.values.subsidiaryCompanyIds || []
                            }
                            disabled={!isEdit}
                            handleSelection={(values) =>
                              formik.setFieldValue(
                                "subsidiaryCompanyIds",
                                values
                              )
                            }
                          />
                        </div>
                      </HStack>
                      <HStack
                        className="items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
                        role="alert"
                      >
                        <Icon
                          icon="fe:warning"
                          width={18}
                          className="mt-0.5 shrink-0"
                        />
                        <p>
                          A subsidiary with employees, grants, plans, templates,
                          or transactions cannot be unlinked. This action is
                          irreversible once the subsidiary has ESOP data.
                        </p>
                      </HStack>
                    </VStack>
                  )}
                  <HStack className="gap-8">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Registered country
                        <span className="text-rose-400">*</span>
                      </Label>
                      <Select
                        options={getNames()}
                        isPlaceHolderDisabled={true}
                        disabled={!isEdit}
                        placeholder="Select registered country"
                        {...formik.getFieldProps("registeredCountry")}
                      />
                      {formik.touched.registeredCountry &&
                        formik.errors.registeredCountry && (
                          <Error
                            text={formik.errors.registeredCountry.toString()}
                          />
                        )}
                      <HStack className="mt-2 items-start gap-1.5 text-xs text-slate-600">
                        <Icon
                          icon="mdi:information-outline"
                          width={16}
                          className="mt-px shrink-0 text-primary"
                        />
                        <p>
                          Grant parameters restrictions will be based on the
                          selected country.
                        </p>
                      </HStack>
                    </div>
                    <div className="flex-1" />
                  </HStack>
                </VStack>
              </div>
              {!isUserEsopViewer &&
                (isEdit ? (
                  <HStack className="justify-end m-8">
                    <ButtonPrimary
                      onClick={() => setIsEdit(!isEdit)}
                      disabled={isUserReadOnly}
                    >
                      Save
                    </ButtonPrimary>
                  </HStack>
                ) : (
                  <HStack className="justify-end m-8">
                    <ButtonPrimary
                      className={
                        !determineUserAccessToResource(
                          permission?.aclList || [],
                          Resource.Company,
                          DefaultAction.All
                        )
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }
                      type="submit"
                      onClick={() => setIsEdit(!isEdit)}
                      disabled={
                        isUserReadOnly ||
                        !determineUserAccessToResource(
                          permission?.aclList || [],
                          Resource.Company,
                          DefaultAction.All
                        )
                      }
                      loading={isLoading}
                    >
                      Edit
                    </ButtonPrimary>
                  </HStack>
                ))}
              {imageResizerProps.open && (
                <Dialog maxWidth="lg" open={imageResizerProps.open}>
                  <div className="w-[600px]">
                    <ImageResizer
                      onCancel={() =>
                        setImageResizerProps({
                          ...imageResizerProps,
                          open: false,
                        })
                      }
                      orientationSettings={{
                        displayFormat:
                          formik.values.companyLogoDisplaySettings
                            ?.displayFormat ?? "logoAndName",
                        orientation:
                          formik.values.companyLogoDisplaySettings
                            ?.orientation ?? "portrait",
                        show: true,
                      }}
                      onSave={(
                        image: string | undefined,
                        orientationSettings
                      ) => handleImageSave(image, formik, orientationSettings)}
                      imageUrl={imageResizerProps.imageUrl}
                    />
                  </div>
                </Dialog>
              )}
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}

export default CompanyProfile;
